/**
 * useResponsive - Custom hook for responsive design
 * @module hooks/starnexus/useResponsive
 *
 */

import { useState, useEffect, useCallback } from 'react';

// Breakpoint definitions
export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1280
} as const;

export interface ResponsiveState {
  /** Current viewport width */
  width: number;
  /** Current viewport height */
  height: number;
  /** Is mobile device (< 768px) */
  isMobile: boolean;
  /** Is tablet device (768px - 1024px) */
  isTablet: boolean;
  /** Is small desktop (1024px - 1280px) */
  isSmallDesktop: boolean;
  /** Is large desktop (>= 1280px) */
  isLargeDesktop: boolean;
  /** Should sidebar be collapsed (< 1280px) */
  shouldCollapseSidebar: boolean;
  /** Number of columns for metric cards */
  metricColumns: 1 | 2 | 4;
  /** Is wizard disabled (mobile only) */
  isWizardDisabled: boolean;
}

/**
 * Hook to track responsive breakpoints and provide layout hints
 */
export const useResponsive = (): ResponsiveState => {
  const getState = useCallback((): ResponsiveState => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const height = typeof window !== 'undefined' ? window.innerHeight : 1080;

    const isMobile = width < BREAKPOINTS.mobile;
    const isTablet = width >= BREAKPOINTS.mobile && width < BREAKPOINTS.tablet;
    const isSmallDesktop = width >= BREAKPOINTS.tablet && width < BREAKPOINTS.desktop;
    const isLargeDesktop = width >= BREAKPOINTS.desktop;

    // Determine metric columns based on screen size
    let metricColumns: 1 | 2 | 4 = 4;
    if (isMobile) {
      metricColumns = 1;
    } else if (isTablet || isSmallDesktop) {
      metricColumns = 2;
    }

    return {
      width,
      height,
      isMobile,
      isTablet,
      isSmallDesktop,
      isLargeDesktop,
      shouldCollapseSidebar: width < BREAKPOINTS.desktop,
      metricColumns,
      isWizardDisabled: isMobile
    };
  }, []);

  const [state, setState] = useState<ResponsiveState>(getState);

  useEffect(() => {
    const handleResize = () => {
      setState(getState());
    };

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Initial state
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [getState]);

  return state;
};

export default useResponsive;
